import { Decimal } from "@kase/my-marathon-communication";
import { addDays, isAfter, isBefore, isWeekend, startOfDay } from "date-fns";
import { TaskAssignment, TimeContract, getDayContract } from "~/core";
import { CapacityRange } from "./interfaces";

// used when employee has no contract data
const FALLBACK_DAY_HOURS = 8;
const DONE_FLAG = 0b1;
// guards against broken assignments (e.g. tiny daymax)
const MAX_ASSIGNMENT_DAYS = 3 * 366;

const getDayHours = (contracts: TimeContract[], day: Date) => {
    if (contracts.length === 0) {
        return isWeekend(day) ? 0 : FALLBACK_DAY_HOURS;
    }

    const contract = getDayContract(contracts, day);

    return contract?.contracted_day
        ? new Decimal(contract.contracted_day).toNumber()
        : 0;
};

export const getRangeYears = ({ start, end }: CapacityRange) =>
    Array.from(
        { length: end.getFullYear() - start.getFullYear() + 1 },
        (_, i) => start.getFullYear() + i,
    );

// assignment is filled day by day from its start, at most `daymax` a day,
// skipping days the employee doesn't work, until `planned` is used up
const getAssignedInRange = (
    assignment: TaskAssignment,
    contracts: TimeContract[],
    start: Date,
    end: Date,
) => {
    if (assignment.flags & DONE_FLAG || assignment.daymax <= 0) {
        return 0;
    }

    let day = startOfDay(new Date(Number(assignment.start)));
    let remaining = assignment.planned;
    let result = 0;

    for (let i = 0; remaining > 0 && i < MAX_ASSIGNMENT_DAYS; ++i) {
        if (isAfter(day, end)) {
            break;
        }

        if (getDayHours(contracts, day) > 0) {
            const allocated = Math.min(assignment.daymax, remaining);

            if (!isBefore(day, start)) {
                result += allocated;
            }

            remaining -= allocated;
        }

        day = addDays(day, 1);
    }

    return result / 3600;
};

export const getCapacity = (
    assignments: TaskAssignment[],
    contracts: TimeContract[],
    range: CapacityRange,
) => {
    const start = startOfDay(range.start);
    const end = startOfDay(range.end);
    let available = 0;

    for (let day = start; !isAfter(day, end); day = addDays(day, 1)) {
        available += getDayHours(contracts, day);
    }

    const occupied = assignments.reduce(
        (acc, assignment) =>
            acc + getAssignedInRange(assignment, contracts, start, end),
        0,
    );

    return {
        available,
        occupied,
        free: Math.max(0, available - occupied),
        percent: available > 0 ? (occupied / available) * 100 : 0,
    };
};
